import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { PROFILE, SOCIALS } from "../data/portfolioData";
import { SOCIAL_ICONS, CopyIcon, CheckIcon } from "../components/ui/Icons";

const EMAILJS = {
  service: "service_ckk9fff",
  template: "template_h7pd39t",
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

const inputClass =
  "flex w-full rounded-md bg-primary text-ink px-3 py-2 text-sm placeholder:text-ink/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 transition";

const Field = ({ label, id, children }) => (
  <div className="flex flex-col space-y-2 w-full">
    <label htmlFor={id} className="text-sm font-medium text-ink/90">
      {label}
    </label>
    {children}
  </div>
);

const Contact = () => {
  const form = useRef(null);
  const [status, setStatus] = useState("idle");
  const [copied, setCopied] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await emailjs.sendForm(EMAILJS.service, EMAILJS.template, form.current, { publicKey: EMAILJS.publicKey });
      setStatus("sent");
      form.current.reset();
    } catch {
      setStatus("error");
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(PROFILE.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { delay: 1.2, duration: 0.4 } }}
      className="pt-32 xl:pt-40 pb-6 px-4"
    >
      <div className="max-w-3xl w-full mx-auto rounded-none md:rounded-2xl p-4 md:p-8 bg-card border border-ink/10">
        <h2 className="font-bold text-xl text-ink">Get in Touch</h2>
        <p className="text-ink/60 text-sm max-w-sm mt-2 leading-relaxed">
          Questions, opportunities or just a hello. I read everything.
        </p>

        <form ref={form} onSubmit={send} className="my-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Field label="Name" id="name">
              <input id="name" name="user_name" required placeholder="Your name" className={`${inputClass} h-10`} />
            </Field>
            <Field label="Email Address" id="email">
              <input
                id="email"
                name="user_email"
                type="email"
                required
                placeholder="you@example.com"
                className={`${inputClass} h-10`}
              />
            </Field>
          </div>
          <Field label="Message" id="message">
            <textarea
              id="message"
              name="message"
              rows={5}
              required
              placeholder="What would you like to talk about?"
              className={`${inputClass} resize-none`}
            />
          </Field>
          <button
            type="submit"
            disabled={status === "sending"}
            className="relative group/btn block w-full text-primary rounded-full h-11 font-medium bg-ink hover:bg-ink/90 transition-colors disabled:opacity-60"
          >
            {status === "sending" ? "Sending…" : "Send Email →"}
            <span className="group-hover/btn:opacity-100 block transition duration-500 opacity-0 absolute h-px w-full -bottom-px inset-x-0 bg-gradient-to-r from-transparent via-accent to-transparent" />
            <span className="group-hover/btn:opacity-100 blur-sm block transition duration-500 opacity-0 absolute h-px w-1/2 mx-auto -bottom-px inset-x-10 bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
          </button>
          {status === "sent" && <p className="text-accent text-sm">Thanks! Your message is on its way.</p>}
          {status === "error" && (
            <p className="text-red-400 text-sm">Something went wrong. Please email me directly instead.</p>
          )}
        </form>

        <div className="my-8 h-px w-full bg-gradient-to-r from-transparent via-ink/20 to-transparent" />

        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between px-4 w-full rounded-md h-10 bg-primary">
            <span className="text-ink/75 text-sm truncate">Email: {PROFILE.email}</span>
            <button type="button" onClick={copyEmail} title="Copy email" className="text-accent p-1">
              {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
            </button>
          </div>
          {SOCIALS.filter((s) => s.icon !== "mail").map((s) => {
            const Icon = SOCIAL_ICONS[s.icon];
            return (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 w-full rounded-md h-10 bg-primary text-ink/75 text-sm hover:text-accent transition-colors"
              >
                <Icon size={16} />
                {s.name}
              </a>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

export default Contact;
